<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import { summaryApi } from '../api/SummaryApi'
import { emptyWeeklySummary, normalizeWeeklySummary } from '../service/normalizeWeeklySummary'

import {
  summaryPanelActionsClass,
  summaryPanelClass,
  summaryPanelDetailsClass,
} from './summaryPanelClasses'

import { useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Badge, Button, Icon, PlatformLogo, Skeleton } from '@/shared/ui'
import { RichTextViewer } from '@/shared/ui/editor'
import type { UiAsyncState, UiTone } from '@/shared/ui/types'

const emit = defineEmits<{ 'visibility-change': [value: boolean] }>()
const workspace = useWorkspace()
const { t } = useI18n()
const format = useFormat(workspace.currency.value ? { currency: workspace.currency.value } : {})
const state = ref<UiAsyncState>('loading')
const expanded = ref(false)
const summary = ref(emptyWeeklySummary())
let controller: AbortController | null = null

function emitVisibility(value: boolean): void {
  emit('visibility-change', value)
}

const dateLabel = computed(() => {
  const { startDate, endDate } = summary.value.dateRange
  if (expanded.value && startDate && endDate)
    return `${format.formatDate(startDate)} – ${format.formatDate(endDate)}`
  return summary.value.createdAt
    ? t('summary.weekly.generated', { date: format.formatDate(summary.value.createdAt) })
    : ''
})

function badgeTone(tone: string): UiTone {
  return tone === 'positive' ? 'success' : tone === 'negative' ? 'danger' : 'neutral'
}

function delta(value: number | null): string {
  if (value === null || value === 0) return ''
  const formatted = format.formatNumber(value)
  return t('summary.weekly.delta', { value: value > 0 ? `+${formatted}` : formatted })
}

async function load(): Promise<void> {
  const workspaceId = workspace.workspaceId.value
  if (workspaceId === undefined) {
    state.value = 'empty'
    summary.value = emptyWeeklySummary()
    emitVisibility(false)
    return
  }
  controller?.abort()
  controller = new AbortController()
  state.value = 'loading'
  expanded.value = false
  emitVisibility(true)
  try {
    const payload = await summaryApi.getLatestWeekly(workspaceId, controller.signal)
    summary.value = normalizeWeeklySummary(payload, {
      hideAfterWeeklyCutoff: true,
      timeZone: String(workspace.workspace.value?.timezone ?? 'UTC'),
    })
    state.value = summary.value.shouldRender ? 'ready' : 'empty'
    emitVisibility(summary.value.shouldRender)
  } catch {
    if (controller.signal.aborted) return
    summary.value = emptyWeeklySummary()
    state.value = 'error'
    emitVisibility(false)
  }
}

watch(
  () => workspace.workspaceId.value,
  () => void load(),
  { immediate: true },
)
onBeforeUnmount(() => controller?.abort())
</script>

<template>
  <section
    v-if="state === 'loading' || summary.shouldRender"
    :class="[
      summaryPanelClass,
      'summary-weekly overflow-hidden rounded-md px-md py-sm',
      { 'summary-weekly--expanded py-md': expanded },
    ]"
  >
    <div v-if="state === 'loading'" class="flex flex-wrap items-center justify-between gap-sm">
      <div class="grid min-w-0 gap-xs">
        <Skeleton width="9rem" height="1.25rem" />
        <Skeleton width="7rem" height="1rem" />
      </div>
      <Skeleton width="5rem" height="2.25rem" />
    </div>
    <template v-else>
      <header class="flex flex-wrap items-center justify-between gap-sm">
        <div class="min-w-0">
          <h2 class="m-0 text-primary text-xl font-semibold leading-7">
            {{ t('summary.weekly.title') }}
          </h2>
          <p class="m-0 mt-2xs text-muted text-sm font-light">{{ dateLabel }}</p>
        </div>
        <div :class="[summaryPanelActionsClass, 'summary-panel__actions']">
          <RouterLink
            v-if="expanded"
            :class="[summaryPanelDetailsClass, 'summary-panel__details']"
            :to="summary.href"
          >
            <span>{{ t('summary.weekly.fullReport') }}</span>
            <Icon name="arrow-right" :size="12" />
          </RouterLink>
          <Button
            class="summary-weekly__toggle !h-9 !px-sm !rounded-full !bg-surface hover:!border-primary hover:!text-primary"
            size="sm"
            variant="outline"
            :aria-controls="'summary-weekly-content'"
            :aria-expanded="expanded"
            @click="expanded = !expanded"
          >
            {{ expanded ? t('summary.actions.hide') : t('summary.actions.display') }}
            <template #end>
              <Icon :name="expanded ? 'chevron-up' : 'chevron-down'" :size="12" />
            </template>
          </Button>
        </div>
      </header>
      <div v-if="expanded" id="summary-weekly-content" class="mt-md">
        <div class="grid grid-cols-2 min-[80rem]:grid-cols-4 gap-sm">
          <article
            v-for="metric in summary.metrics"
            :key="metric.id"
            class="p-sm border border-border rounded-xl bg-surface shadow-input"
          >
            <p class="m-0 flex min-w-0 items-center gap-1.5 text-muted text-sm font-medium">
              <PlatformLogo
                v-if="metric.logoSrc"
                :platform="metric.logoKey"
                :src="metric.logoSrc"
                :size="20"
                alt=""
              />
              <span class="overflow-hidden text-ellipsis whitespace-nowrap">{{
                metric.label
              }}</span>
            </p>
            <p class="m-0 mt-2xs text-fg text-[1.75rem] font-semibold leading-none">
              {{ metric.value }}
            </p>
            <Badge
              v-if="delta(metric.pctChange)"
              class="mt-sm text-xs font-semibold"
              :tone="badgeTone(metric.deltaTone)"
              >{{ delta(metric.pctChange) }}</Badge
            >
          </article>
        </div>
        <RichTextViewer
          v-if="summary.summaryText"
          class="mt-md text-fg text-sm leading-6 line-clamp-2"
          :content="summary.summaryText"
        />
      </div>
    </template>
  </section>
</template>
